import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import TodayList from "app/views/employees/attendance/todayList";
import MonthlyList from "app/views/employees/attendance/monthlyList";
import ComplienceReport from "./attendance/ComplienceReport";
import * as moment from "moment";
import { COLORS, FONTS, SHADOWS, RADII } from "app/views/dashboard/shared/tokens";
import { getConfig, getAttendanceStatCounts } from "app/services/Services";

/* ─── Attendance-specific status colors ─── */
export const STATUS_COLORS = {
  ontime:     { dot: "#2fbd77", ink: "#2f7d5a", bg: "#eaf8f0", border: "#cceedb" },
  late:       { dot: "#e0a34a", ink: "#9b5a06", bg: "#fdf3e5", border: "#f6e2c1" },
  nocheckout: { dot: "#e05468", ink: "#c23b50", bg: "#fdf1f3", border: "#f7d8de" },
  absent:     { dot: "#8a839c", ink: "#544d68", bg: "#f4f1f9", border: "#e6d9f2" },
  onleave:    { dot: "#9895f4", ink: "#5a4fbd", bg: "#f0eefe", border: "#ddd8fa" },
};

export const COMPLIANCE_COLORS = {
  compliant:    { dot: "#2fbd77", ink: "#2f7d5a", bg: "#eaf8f0", border: "#cceedb" },
  atrisk:       { dot: "#e0a34a", ink: "#9b5a06", bg: "#fdf3e5", border: "#f6e2c1" },
  actionneeded: { dot: "#e05468", ink: "#c23b50", bg: "#fdf1f3", border: "#f7d8de" },
  openshifts:   { dot: "#8a839c", ink: "#544d68", bg: "#f4f1f9", border: "#e6d9f2" },
  avghours:     { dot: "#9895f4", ink: "#5a4fbd", bg: "#f0eefe", border: "#ddd8fa" },
};

/* ─── Shared card style for child views ─── */
export const TABLE_CARD_SX = {
  background: COLORS.surface,
  border: `1px solid ${COLORS.cardBorder}`,
  borderRadius: RADII.card,
  boxShadow: SHADOWS.card,
  overflow: "hidden",
  minWidth: 0,
};

/* ─── Default shift config ─── */
export const DEFAULT_SHIFT = { startHour: 9, startMin: 0, graceMin: 15, expectedHours: 8.5 };

const VIEWS = [
  { key: "daily", label: "Daily log" },
  { key: "monthly", label: "Month log" },
  { key: "compliance", label: "Compliance report" },
];

const DAILY_STATS = [
  { key: "ontime",     label: "On time",       unit: "employees" },
  { key: "late",       label: "Late arrivals",  unit: "employees" },
  { key: "nocheckout", label: "Open shifts",    unit: "active" },
  { key: "absent",     label: "Absent",         unit: "today" },
  { key: "onleave",    label: "On leave",       unit: "approved" },
];

const COMPLIANCE_STATS = [
  { key: "compliant",    label: "Compliant",     unit: "employees" },
  { key: "atrisk",       label: "At risk",       unit: "employees" },
  { key: "actionneeded", label: "Action needed",  unit: "employees" },
  { key: "openshifts",   label: "Open shifts",    unit: "total" },
  { key: "avghours",     label: "Avg hours/day",  unit: "hours", readOnly: true },
];

/* ─── SVG icons ─── */
const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a411c1" strokeWidth="2" strokeLinecap="round">
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M3.5 10h17M8.5 3v4M15.5 3v4" />
  </svg>
);
const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M14 6l-6 6 6 6" /></svg>
);
const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M10 6l6 6-6 6" /></svg>
);
const DownloadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 3v12M7.5 11l4.5 4.5 4.5-4.5M4 20h16" /></svg>
);
const ClockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="8.5" /><path d="M12 8v4.2l3 1.8" /></svg>
);
const BreadcrumbArrow = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#c1b8d1" strokeWidth="2.4" strokeLinecap="round"><path d="M10 6l6 6-6 6" /></svg>
);

export default function AttendanceList() {
  const [activeView, setActiveView] = useState("daily");
  const [activeDate, setActiveDate] = useState(new Date());
  const [activeFilters, setActiveFilters] = useState([]);
  const [statCounts, setStatCounts] = useState({});
  const [totalEmployeeCount, setTotalEmployeeCount] = useState(0);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [config, setConfigState] = useState({ time: true, photo: true });

  const exportRef = useRef(null);

  /* Load company config once */
  useEffect(() => {
    getConfig()
      .then((res) => {
        if (res?.status === 200) {
          setConfigState(res?.data?.data?.configuration?.is_display || { time: true, photo: true });
        }
      })
      .catch(() => setConfigState({ time: true, photo: true }));
  }, []);

  /* ── Fetch server-side stat counts ── */
  useEffect(() => {
    const dateParam = activeView === "daily"
      ? moment(activeDate).format("YYYY-MM-DD")
      : moment(activeDate).format("YYYY-MM");
    getAttendanceStatCounts({ date: dateParam })
      .then((res) => {
        if (res?.status === 200 && res?.data?.data) {
          const d = res.data.data;
          setStatCounts({
            ontime: d.onTime ?? 0,
            late: d.late ?? 0,
            nocheckout: d.openShifts ?? 0,
            absent: d.absent ?? 0,
            onleave: d.onLeave ?? 0,
          });
          if (d.totalEmployees) setTotalEmployeeCount(d.totalEmployees);
        }
      })
      .catch(() => {
        // Fall back to client-side counts from child components
      });
  }, [activeDate, activeView]);

  /* ── Date stepper ── */
  const isDaily = activeView === "daily";
  const isCompliance = activeView === "compliance";

  const scopeLabel = isDaily
    ? moment(activeDate).format("ddd, DD MMMM YYYY")
    : moment(activeDate).format("MMMM YYYY");

  const isToday = moment(activeDate).isSame(moment(), "day");
  const isCurrentMonth = moment(activeDate).isSame(moment(), "month");
  const nextDisabled = isDaily ? isToday : isCurrentMonth;

  const stepPrev = useCallback(() => {
    setActiveDate((d) => {
      const nd = new Date(d);
      if (isDaily) nd.setDate(nd.getDate() - 1);
      else nd.setMonth(nd.getMonth() - 1);
      return nd;
    });
  }, [isDaily]);

  const stepNext = useCallback(() => {
    if (nextDisabled) return;
    setActiveDate((d) => {
      const nd = new Date(d);
      if (isDaily) nd.setDate(nd.getDate() + 1);
      else nd.setMonth(nd.getMonth() + 1);
      return nd;
    });
  }, [isDaily, nextDisabled]);

  /* ── Filter toggling ── */
  const handleStatClick = useCallback((key) => {
    setActiveFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [key]));
  }, []);
  const clearFilters = useCallback(() => setActiveFilters([]), []);

  /* ── View switch ── */
  const handleViewChange = useCallback((key) => {
    setActiveView(key);
    setActiveFilters([]);
    setActiveDate(new Date());
    setStatCounts({});
  }, []);

  const crumbLabel = isDaily ? "Daily Log" : isCompliance ? "Compliance Report" : "Month Log";
  const headerMeta = totalEmployeeCount > 0
    ? `${totalEmployeeCount} employees \u00b7 ${isDaily ? moment(activeDate).format("dddd, D MMMM YYYY") : moment(activeDate).format("MMMM YYYY")}`
    : "";

  const statsConfig = isCompliance ? COMPLIANCE_STATS : DAILY_STATS;
  const colorMap = isCompliance ? COMPLIANCE_COLORS : STATUS_COLORS;

  /* ── Stat cell ── */
  const renderStatCell = (stat) => {
    const c = colorMap[stat.key];
    const isActive = activeFilters.includes(stat.key);
    const count = statCounts[stat.key] ?? 0;
    const isReadOnly = stat.readOnly;

    return (
      <Box
        key={stat.key}
        sx={{
          p: "16px 18px",
          background: isActive
            ? "linear-gradient(135deg, rgba(224,89,240,0.06), rgba(152,149,244,0.06))"
            : COLORS.surface,
          cursor: isReadOnly ? "default" : "pointer",
          borderBottom: isActive ? `3px solid ${c.dot}` : "3px solid transparent",
          transition: "background .15s, border-color .15s",
          "&:hover": isReadOnly ? {} : { background: "#faf8fd" },
        }}
        onClick={isReadOnly ? undefined : () => handleStatClick(stat.key)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px", mb: "9px" }}>
          <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: c.dot }} />
          <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 600, color: COLORS.body2 }}>{stat.label}</Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
          <Box component="span" sx={{ fontFamily: FONTS.heading, fontSize: "26px", fontWeight: 600, letterSpacing: "-0.02em", color: COLORS.ink, lineHeight: 1 }}>
            {stat.key === "avghours" ? (typeof count === "number" ? count.toFixed(1) : "0.0") : count}
          </Box>
          <Box component="span" sx={{ fontSize: "12.5px", color: COLORS.muted }}>{stat.unit}</Box>
        </Box>
      </Box>
    );
  };

  /* ── Styles ── */
  const segActive = {
    fontSize: "13.5px", fontWeight: 600, color: "#fff", background: COLORS.gradient,
    p: "8px 16px", borderRadius: "9px", cursor: "pointer", boxShadow: SHADOWS.tabActive, userSelect: "none",
  };
  const segInactive = {
    fontSize: "13.5px", fontWeight: 500, color: COLORS.body2, background: "transparent",
    p: "8px 16px", borderRadius: "9px", cursor: "pointer", userSelect: "none",
    "&:hover": { color: COLORS.ink3, background: "#faf8fd" },
  };
  const stepBtn = {
    width: 28, height: 28, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: COLORS.body2, border: "none", background: "transparent",
    "&:hover": { background: "#f8f5fd", color: COLORS.ink3 },
  };
  const stepBtnOff = { ...stepBtn, color: "#dcd3ee", cursor: "default", pointerEvents: "none", "&:hover": {} };

  return (
    <Box sx={{
      fontFamily: FONTS.body, color: COLORS.ink3, background: COLORS.background, minHeight: "100vh",
      p: { xs: "20px 16px", md: "26px 30px 44px" }, display: "flex", flexDirection: "column", gap: "16px",
    }}>
      {/* HEADER */}
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "20px", flexWrap: "wrap" }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: COLORS.muted, mb: "7px" }}>
            <span>Attendance Management</span>
            <BreadcrumbArrow />
            <Box component="span" sx={{ color: COLORS.body, fontWeight: 500 }}>{crumbLabel}</Box>
          </Box>
          <Box component="h1" sx={{ fontFamily: FONTS.heading, fontSize: "28px", m: "0 0 5px", fontWeight: 600, letterSpacing: "-0.015em", color: COLORS.ink }}>
            Attendance
          </Box>
          <Box component="p" sx={{ m: 0, fontSize: "14.5px", color: COLORS.body2 }}>{headerMeta}</Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap" }}>
          <Box component="button" onClick={() => exportRef.current?.()} sx={{
            display: "flex", alignItems: "center", gap: "8px", fontSize: "13.5px", fontWeight: 500,
            color: COLORS.body, background: COLORS.surface, border: `1px solid ${COLORS.cardBorder}`,
            p: "10px 14px", borderRadius: RADII.control, cursor: "pointer",
            "&:hover": { borderColor: "#dcd3ee", color: COLORS.ink3 },
          }}>
            <DownloadIcon /> Export
          </Box>
          {isDaily && (
            <Box component="button" onClick={() => setShowManualDialog(true)} sx={{
              display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 600,
              color: "#fff", background: COLORS.gradient, p: "11px 18px", borderRadius: RADII.control,
              cursor: "pointer", border: "none", boxShadow: "0 10px 22px -12px rgba(224,89,240,0.8)",
            }}>
              <ClockIcon /> Manual check-in / out
            </Box>
          )}
        </Box>
      </Box>

      {/* VIEW SWITCH + DATE STEPPER */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", background: COLORS.surface, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "11px", p: "3px", gap: "2px" }}>
          {VIEWS.map((v) => (
            <Box key={v.key} component="span" sx={v.key === activeView ? segActive : segInactive} onClick={() => handleViewChange(v.key)}>
              {v.label}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", background: COLORS.surface, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "11px", p: "5px 6px" }}>
          <Box component="button" sx={stepBtn} onClick={stepPrev}><ChevronLeft /></Box>
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13.5px", fontWeight: 600, color: COLORS.ink3, px: "4px", minWidth: "150px", justifyContent: "center" }}>
            <CalendarIcon /> {scopeLabel}
          </Box>
          <Box component="button" sx={nextDisabled ? stepBtnOff : stepBtn} onClick={stepNext}><ChevronRight /></Box>
        </Box>
      </Box>

      {/* STAT STRIP */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(3,1fr)", lg: "repeat(5,1fr)" },
        gap: "1px", background: COLORS.cardBorder, border: `1px solid ${COLORS.cardBorder}`,
        borderRadius: RADII.card, overflow: "hidden",
      }}>
        {statsConfig.map(renderStatCell)}
      </Box>

      {/* CHILD VIEW */}
      {isDaily && (
        <TodayList
          activeDate={activeDate}
          activeFilters={activeFilters}
          onStatCounts={setStatCounts}
          onTotalCount={setTotalEmployeeCount}
          onClearFilters={clearFilters}
          config={config}
          showManualDialog={showManualDialog}
          onCloseManualDialog={() => setShowManualDialog(false)}
          exportRef={exportRef}
        />
      )}
      {activeView === "monthly" && (
        <MonthlyList
          activeDate={activeDate}
          activeFilters={activeFilters}
          onStatCounts={setStatCounts}
          onTotalCount={setTotalEmployeeCount}
          onClearFilters={clearFilters}
          config={config}
          exportRef={exportRef}
        />
      )}
      {isCompliance && (
        <ComplienceReport
          activeDate={activeDate}
          activeFilters={activeFilters}
          onStatCounts={setStatCounts}
          onTotalCount={setTotalEmployeeCount}
          onClearFilters={clearFilters}
          exportRef={exportRef}
        />
      )}
    </Box>
  );
}
